/**
 * QUICK ADD MODAL - CHANGELOG
 * 
 * Natulim SP - 19-nov-25: Fix autoplay videos en modal de vista rápida
 * - PROBLEMA: Los videos con atributo autoplay (secciones video-with-text) no se reproducían
 *   automáticamente en el modal, aunque sí funcionaban en las páginas normales (PDP)
 * - CAUSA: HTMLUpdateUtility.setInnerHTML() rompe el autoplay nativo del navegador
 * - SOLUCIÓN: Implementar initializeVideos() que se ejecuta después de mostrar el modal
 *   con setTimeout(100ms) para asegurar que el DOM esté completamente renderizado
 * - FUNCIONES AÑADIDAS:
 *   * initializeVideos(): Reproduce automáticamente todos los videos muted en el modal

 * 
 * Natulim SP - 18-nov-25: Fix videos circulares en modal de vista rápida
 * - PROBLEMA: Los videos circulares no funcionaban en el modal porque el modal global de videos
 *   (.modalWrapperVideo) solo se creaba en main-product.liquid, no en el modal de vista rápida
 * - SOLUCIÓN: Modificar initializeCircleVideos() para crear automáticamente el modal global
 *   si no existe, usando la misma lógica de main-product.liquid
 * - FUNCIONES AÑADIDAS:
 *   * initializeCircleVideos(): Detecta y crea modal global si es necesario
 *   * createGlobalVideoModal(): Crea toda la estructura del modal de videos
 *   * playNextVideoFallback(): Fallback para auto-play si función global no existe
 * - RESULTADO: Videos circulares funcionan completamente en modal (click, play/pause, sonido, etc.)
 */

// Product IDs that should bypass modal and go directly to product page
// Natulim - 4-dic-25: Lista vaciada para que todos los productos (incluyendo productos ker) abran el modal
const BYPASS_MODAL_PRODUCT_IDS = [

];

// Natulim SP - 4-dic-25: CSS y JS específicos para productos KER
// Estos recursos se cargan dinámicamente cuando se abre un producto KER en el modal
const KER_ASSETS = {
  css: ['ker-styles.css'],
  // Handles de productos KER (de la colección cabello-y-cuerpo)
  productHandles: ['champu', 'gel-de-ducha', 'jabon-de-manos-ker', 'dispensador-ker'],
  // Indicadores en el HTML para detectar productos KER
  htmlIndicators: ['.ker-steps-container', '.wrap-price', '.price-ker', '[class*="ker-"]'],
  // Cache de URLs de assets ya detectados
  assetUrls: {}
};

// Función para cargar CSS dinámicamente
function loadKerAssets(htmlDoc) {
  KER_ASSETS.css.forEach(cssFile => {
    const cssId = `ker-styles-modal-${cssFile.replace(/\./g, '-')}`;
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';

      // Intentar obtener la URL del CSS del HTML cargado
      let cssUrl = null;

      // Buscar en el HTML cargado la referencia al CSS (incluyendo head)
      if (htmlDoc) {
        const existingLink = htmlDoc.querySelector(`link[href*="${cssFile}"]`);
        if (existingLink) {
          // getAttribute('href') da el valor original, .href da URL absoluta
          cssUrl = existingLink.getAttribute('href') || existingLink.href;
          KER_ASSETS.assetUrls[cssFile] = cssUrl;
          console.log(`🔍 KER CSS found in fetched HTML: ${cssUrl}`);
        }
      }

      // Usar URL cacheada si existe
      if (!cssUrl && KER_ASSETS.assetUrls[cssFile]) {
        cssUrl = KER_ASSETS.assetUrls[cssFile];
        console.log(`📦 Using cached KER CSS URL: ${cssUrl}`);
      }

      // Buscar en la página actual una referencia a assets de Shopify
      if (!cssUrl) {
        // Método 1: Buscar un link existente con la estructura de assets de Shopify
        const existingAssets = document.querySelectorAll('link[href*="/assets/"]');
        for (const asset of existingAssets) {
          const href = asset.href;
          // Buscar patrón: //cdn.shopify.com/s/files/1/xxxx/xxxx/t/xx/assets/
          const match = href.match(/(\/\/[^/]+\/s\/files\/1\/[^/]+\/[^/]+\/t\/[^/]+\/assets\/)/);
          if (match) {
            cssUrl = `https:${match[1]}${cssFile}`;
            console.log(`🔧 Built KER CSS URL from existing asset: ${cssUrl}`);
            break;
          }
        }
      }

      // Método 2: Usar Shopify.cdnHost si está disponible
      if (!cssUrl && window.Shopify?.cdnHost) {
        const shop = window.Shopify?.shop || '';
        // Necesitamos el theme ID, intentar extraerlo de otro asset
        const themeAsset = document.querySelector('link[href*="/t/"]');
        if (themeAsset) {
          const themeMatch = themeAsset.href.match(/\/t\/(\d+)\//);
          if (themeMatch) {
            cssUrl = `https://${window.Shopify.cdnHost}/s/files/1/${shop.replace('.myshopify.com', '').replace('.', '/')}/t/${themeMatch[1]}/assets/${cssFile}`;
          }
        }
      }

      // Fallback: construir URL basada en el patrón estándar detectado
      if (!cssUrl) {
        // Buscar cualquier script o link con el patrón de CDN
        const cdnElement = document.querySelector('[src*="cdn.shopify.com"], [href*="cdn.shopify.com"]');
        if (cdnElement) {
          const cdnUrl = cdnElement.src || cdnElement.href;
          const match = cdnUrl.match(/(https?:\/\/cdn\.shopify\.com\/s\/files\/1\/[^/]+\/[^/]+\/t\/\d+\/assets\/)/);
          if (match) {
            cssUrl = `${match[1]}${cssFile}`;
            console.log(`🔧 Built KER CSS URL from CDN pattern: ${cssUrl}`);
          }
        }
      }

      if (cssUrl) {
        link.href = cssUrl;
        document.head.appendChild(link);
        console.log(`✅ KER CSS loaded: ${cssFile} from ${cssUrl}`);
      } else {
        console.warn(`⚠️ Could not determine URL for KER CSS: ${cssFile}`);
      }
    } else {
      console.log(`ℹ️ KER CSS already loaded: ${cssFile}`);
    }
  });
}

// Función para detectar si el HTML cargado es de un producto KER
function isKerProduct(html, productUrl) {
  // Método 1: Verificar por handle del producto en la URL
  if (productUrl) {
    const handleMatch = productUrl.match(/\/products\/([^/?#]+)/);
    if (handleMatch && KER_ASSETS.productHandles.includes(handleMatch[1])) {
      return true;
    }
  }

  // Método 2: Verificar indicadores en el HTML
  if (html) {
    const parser = new DOMParser();
    const doc = typeof html === 'string' ? parser.parseFromString(html, 'text/html') : html;

    for (const indicator of KER_ASSETS.htmlIndicators) {
      if (doc.querySelector(indicator)) {
        return true;
      }
    }

    // Método 3: Verificar si el HTML contiene referencias a ker-styles.css
    if (typeof html === 'string' && html.includes('ker-styles.css')) {
      return true;
    }
  }

  return false;
}

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        // Get the product URL from the opener button
        const productUrl = opener.getAttribute('data-product-url');

        // Extract product ID from URL (e.g., /products/product-name?variant=123456)
        const productMatch = productUrl.match(/\/products\/([^/?]+)/);
        if (productMatch) {
          const productHandle = productMatch[1];

          // Fetch product data to get the ID
          fetch(`${productUrl}?section_id=main-product`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const productInfo = html.querySelector('[data-product-id]');
              const productId = productInfo?.getAttribute('data-product-id');

              // Check if this product ID is in the bypass list
              if (productId && BYPASS_MODAL_PRODUCT_IDS.includes(productId)) {
                // Navigate directly to product page
                window.location.href = productUrl;
                return;
              }

              // Natulim - 24-dic-25: Restrict KER modal to specific collection
              // If it's a KER product and we are NOT in 'compra-y-gana' collection, redirect to product page
              if (isKerProduct(responseText, productUrl)) {
                const currentPath = window.location.pathname;
                // Check for the collection handle in the path (works for /collections/compra-y-gana, /en/collections/compra-y-gana, etc.)
                if (!currentPath.includes('/collections/compra-y-gana')) {
                  console.log('🚫 Redirecting KER product to page (restricted collection)');
                  window.location.href = productUrl;
                  return;
                }
              }

              // Otherwise, open modal normally
              this.openModalWithProduct(opener, productUrl);
            })
            .catch((error) => {
              console.error('Error checking product:', error);
              // Fallback to modal on error
              this.openModalWithProduct(opener, productUrl);
            });
        }
      }

      openModalWithProduct(opener, productUrl) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => this.processModalContent(opener, responseText))
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      processModalContent(opener, responseText) {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector('product-info');
        const allSectionsElement = responseHTML.querySelectorAll('#MainContent .shopify-section');

        // Natulim SP - 4-dic-25: Detectar y cargar assets de productos KER
        const productUrl = opener.getAttribute('data-product-url');
        if (isKerProduct(responseText, productUrl)) {
          console.log('🎨 KER product detected - loading KER styles...');
          loadKerAssets(responseHTML);
          // Añadir clase identificadora al modal para estilos específicos
          this.modalContent.classList.add('ker-product-modal');
        } else {
          this.modalContent.classList.remove('ker-product-modal');
        }

        this.preprocessHTML(productElement);

        // Use HTMLUpdateUtility for proper script/custom element handling
        HTMLUpdateUtility.setInnerHTML(
          this.modalContent,
          `<div id="seccion-objetivo" style="display: block;"></div> ${productElement.outerHTML}`
        );

        allSectionsElement.forEach(section => {
          const childPrInfo = section.querySelector('product-info');
          if (Boolean(childPrInfo)) {
            // Don't repeat product info
          } else {
            this.preprocessHTML(section);
            const otherSection = document.createElement('section');
            otherSection.className = section.className;
            this.modalContent.append(otherSection);

            // Use HTMLUpdateUtility for sections too
            HTMLUpdateUtility.setInnerHTML(otherSection, section.innerHTML);
          }
        });

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        // Initialize Judge.me after DOM is updated
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          this.initializeJudgeMeWidgets();
          this.initializeCircleVideos();

          // Natulim SP - 4-dic-25: Reinicializar KER plugins si es un producto KER
          if (this.modalContent.classList.contains('ker-product-modal')) {
            this.initializeKerPlugins();
            this.initializeFaqToggles();
          }
        });

        super.show(opener);

        // Natulim SP - 19-nov-25: Reinicializar autoplaly de videos de sección
        // Reproducir videos después de que el modal se muestre
        // Usar setTimeout para asegurar que el DOM esté completamente renderizado
        setTimeout(() => {
          this.initializeVideos();
        }, 100);

        document.dispatchEvent(new Event('quickAddModalOpened'));

        // Natulim DB | 28 enero 2026 cambio: Activar sincronización de precios en el modal con STAY AI
        if (window.initNatulimPriceObserver) {
          this.initializePriceSync();
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // DO NOT remove Judge.me or Recharge widgets
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);

        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;

        // Handle Judge.me widget ID prefixing
        const judgemeWidgets = productElement.querySelectorAll('[id^="judgeme_"]');
        judgemeWidgets.forEach((widget) => {
          const oldWidgetId = widget.id;
          const newWidgetId = `quickadd-${oldWidgetId}`;
          widget.id = newWidgetId;

          if (widget.innerHTML) {
            widget.innerHTML = widget.innerHTML.replaceAll(oldWidgetId, newWidgetId);
          }
        });
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeJudgeMeWidgets() {
        console.log('🔍 Initializing Judge.me widgets in modal...');

        // Force load lazy images
        const lazyImages = this.modalContent.querySelectorAll('.jdgm-rev__pic-img[data-src]');
        lazyImages.forEach(img => {
          if (!img.src && img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
        });

        // Translate UI elements
        this.translateJudgeMeTitles();

        // Check if Judge.me is loaded
        if (!window.jdgm) {
          console.warn('⏳ Judge.me not loaded yet, retrying in 500ms...');
          setTimeout(() => this.initializeJudgeMeWidgets(), 500);
          return;
        }

        console.log('✅ Judge.me library found');

        const judgemeWidgets = this.modalContent.querySelectorAll('[id^="quickadd-judgeme_"], [class*="jdgm-widget"], .jdgm-rev-widg');
        console.log(`📦 Found ${judgemeWidgets.length} Judge.me widget(s)`);

        if (judgemeWidgets.length === 0) {
          console.warn('⚠️ No Judge.me widgets found in modal');
          return;
        }

        // Ensure all widgets are visible
        judgemeWidgets.forEach(widget => {
          widget.style.display = 'block';
          widget.style.visibility = 'visible';
          widget.style.opacity = '1';
        });

        let initialized = false;

        // Method 1: Try executeWidgets (most reliable)
        if (!initialized && typeof window.jdgm.executeWidgets === 'function') {
          console.log('Method 1: Calling jdgm.executeWidgets()');
          try {
            window.jdgm.executeWidgets();
            initialized = true;
            console.log('✅ Method 1 successful');
          } catch (e) {
            console.error('Method 1 failed:', e);
          }
        }

        // Method 2: Try docReady callback
        if (!initialized && typeof window.jdgm.docReady === 'function') {
          console.log('Method 2: Calling jdgm.docReady()');
          try {
            window.jdgm.docReady(() => {
              if (typeof window.jdgm.executeWidgets === 'function') {
                window.jdgm.executeWidgets();
              }
            });
            initialized = true;
            console.log('✅ Method 2 successful');
          } catch (e) {
            console.error('Method 2 failed:', e);
          }
        }

        // Method 3: Try renderWidgets
        if (!initialized && typeof window.jdgm.renderWidgets === 'function') {
          console.log('Method 3: Calling jdgm.renderWidgets()');
          try {
            window.jdgm.renderWidgets();
            initialized = true;
            console.log('✅ Method 3 successful');
          } catch (e) {
            console.error('Method 3 failed:', e);
          }
        }

        // Method 4: Try render with specific container
        if (!initialized && typeof window.jdgm.render === 'function') {
          console.log('Method 4: Calling jdgm.render(modalContent)');
          try {
            window.jdgm.render(this.modalContent);
            initialized = true;
            console.log('✅ Method 4 successful');
          } catch (e) {
            console.error('Method 4 failed:', e);
          }
        }

        // Method 5: Dispatch custom events
        if (!initialized) {
          console.log('Method 5: Dispatching custom events');
          try {
            const events = ['jdgm:render', 'jdgm:init', 'judgeme:render'];
            events.forEach(eventName => {
              const event = new CustomEvent(eventName, {
                bubbles: true,
                detail: { container: this.modalContent }
              });
              document.dispatchEvent(event);
            });

            // Try executeWidgets after events
            setTimeout(() => {
              if (typeof window.jdgm.executeWidgets === 'function') {
                window.jdgm.executeWidgets();
              }
            }, 100);

            initialized = true;
            console.log('✅ Method 5 executed');
          } catch (e) {
            console.error('Method 5 failed:', e);
          }
        }

        // Method 6: Force attribute trigger
        if (!initialized) {
          console.log('Method 6: Force attribute modification');
          try {
            judgemeWidgets.forEach(widget => {
              widget.setAttribute('data-jdgm-force-render', Date.now());
              void widget.offsetHeight; // Force reflow
            });

            if (typeof window.jdgm.executeWidgets === 'function') {
              setTimeout(() => window.jdgm.executeWidgets(), 50);
            }

            initialized = true;
            console.log('✅ Method 6 executed');
          } catch (e) {
            console.error('Method 6 failed:', e);
          }
        }

        console.log(`🎉 Judge.me initialization complete. Status: ${initialized ? 'SUCCESS' : 'FAILED'}`);

        // Final verification after a delay
        setTimeout(() => {
          const reviews = this.modalContent.querySelectorAll('.jdgm-rev');
          console.log(`📊 Final check: ${reviews.length} review(s) rendered`);
        }, 1000);
      }

      translateJudgeMeTitles() {
        const currentLang = window.Shopify?.locale || 'en';
        const translations = {
          'es': {
            'title': 'Reseñas de Clientes',
            'basedOn': 'Basado en',
            'reviews': 'reseñas',
            'otherLanguages': 'Reseñas en Otros Idiomas'
          },
          'ca': {
            'title': 'Ressenyes de Clients',
            'basedOn': 'Basat en',
            'reviews': 'ressenyes',
            'otherLanguages': 'Ressenyes en Altres Llengües'
          }
        };

        if (!translations[currentLang]) return;

        // Translate main title
        const titleElements = this.modalContent.querySelectorAll('.jdgm-rev-widg__title');
        titleElements.forEach(el => {
          el.textContent = translations[currentLang].title;
        });

        // Translate section titles
        const sectionTitles = this.modalContent.querySelectorAll('.jdgm-rev-widg__section-title');
        sectionTitles.forEach(el => {
          const text = el.textContent.trim().toLowerCase();
          if (text.includes('otros idiomas') || text.includes('altres llengües') || text.includes('other languages')) {
            el.textContent = translations[currentLang].otherLanguages;
          }
        });

        // Translate summary text
        const summaryElements = this.modalContent.querySelectorAll('.jdgm-rev-widg__summary-text');
        summaryElements.forEach(el => {
          const match = el.textContent.match(/(\d+)/);
          if (match) {
            const count = match[1];
            el.textContent = `${translations[currentLang].basedOn} ${count} ${translations[currentLang].reviews}`;
          }
        });
      }

      // Natulim SP - 4-dic-25: Inicializar plugins de KER para productos de cabello y cuerpo
      initializeKerPlugins() {
        console.log('🎨 Initializing KER plugins in modal...');

        // Verificar que los elementos KER existan en el modal
        const kerStepsContainer = this.modalContent.querySelector('.ker-steps-container');
        const kerQuantityPacksBlock = this.modalContent.querySelector('.ker-quantity-packs-block');

        console.log(`📦 KER elements found - steps-container: ${!!kerStepsContainer}, quantity-packs-block: ${!!kerQuantityPacksBlock}`);

        // Inicializar Swiper para el slider de imágenes
        this.initializeModalSwiper();

        // Si ya hay un contenedor de steps, significa que el script inline ya se ejecutó correctamente
        if (kerStepsContainer) {
          console.log('✅ KER steps container already exists - plugin initialized successfully');
          return;
        }

        // Si existe el bloque pero no el container, intentar re-ejecutar los scripts
        if (kerQuantityPacksBlock) {
          console.log('⚠️ KER quantity packs block exists but steps container not found - attempting to re-initialize...');

          // Buscar scripts de KER en el contenido del modal y re-ejecutarlos
          const kerScripts = this.modalContent.querySelectorAll('script');
          kerScripts.forEach((script, index) => {
            const content = script.textContent || script.innerHTML;
            if (content.includes('KerQuantityPacksPlugin') || content.includes('KerStep')) {
              console.log(`🔄 Re-executing KER script #${index + 1}...`);
              try {
                // Crear y ejecutar nuevo script
                const newScript = document.createElement('script');
                newScript.textContent = content;
                this.modalContent.appendChild(newScript);
              } catch (error) {
                console.error(`❌ Error re-executing KER script:`, error);
              }
            }
          });
        }

        // Verificar después de un momento si se creó el contenedor
        setTimeout(() => {
          const kerStepsAfter = this.modalContent.querySelector('.ker-steps-container');
          if (kerStepsAfter) {
            console.log('✅ KER steps container created after re-initialization');
          } else {
            console.warn('⚠️ KER steps container still not found after re-initialization attempt');
            // Último intento: verificar si hay KerQuantityPacksPlugin disponible globalmente
            if (window.KerQuantityPacksPlugin) {
              console.log('🔧 Attempting to use global KerQuantityPacksPlugin...');
            }
          }
        }, 500);
      }

      // Natulim SP - 4-dic-25: Inicializar FAQ toggles en modal KER
      initializeFaqToggles() {
        console.log('📋 Initializing FAQ toggles in modal...');

        // Buscar todos los FAQ toggles en el modal
        const faqToggles = this.modalContent.querySelectorAll('[class*="faq-toggle-template"]');

        if (faqToggles.length === 0) {
          console.log('ℹ️ No FAQ toggles found in modal');
          return;
        }

        console.log(`📋 Found ${faqToggles.length} FAQ toggles`);

        faqToggles.forEach((toggle, index) => {
          // Remover listeners anteriores clonando el elemento
          const newToggle = toggle.cloneNode(true);
          toggle.parentNode.replaceChild(newToggle, toggle);

          newToggle.addEventListener('click', () => {
            const faqItem = newToggle.closest('[class*="faq-item-template"]');
            if (!faqItem) return;

            const answer = faqItem.querySelector('[class*="faq-answer-template"]');
            const arrow = newToggle.querySelector('[class*="faq-arrow-template"]');
            const heightCal = faqItem.querySelector('[class*="faq-height-cal-template"]');

            if (!answer) return;

            // Toggle estado
            const isOpen = answer.style.display === 'block';

            if (isOpen) {
              // Cerrar
              answer.style.display = 'none';
              answer.style.height = '0px';
              answer.style.paddingTop = '0px';
              answer.style.paddingBottom = '0px';
              if (arrow) arrow.style.setProperty('--rotate', '0deg');
            } else {
              // Abrir - calcular altura real
              const realHeight = heightCal ? heightCal.offsetHeight : 'auto';
              answer.style.display = 'block';
              answer.style.height = (typeof realHeight === 'number' ? realHeight + 'px' : realHeight);
              answer.style.paddingTop = '16px';
              answer.style.paddingBottom = '0px';
              if (arrow) arrow.style.setProperty('--rotate', '180deg');
            }
          });
        });

        console.log('✅ FAQ toggles initialized in modal');
      }

      // Natulim SP - 4-dic-25: Inicializar Swiper para slider de imágenes en modal KER
      initializeModalSwiper() {
        const swiperContainer = this.modalContent.querySelector('.slider-with-text__swiper');

        if (!swiperContainer) {
          console.log('ℹ️ No Swiper container found in modal');
          return;
        }

        // Verificar si Swiper está cargado
        if (typeof Swiper === 'undefined') {
          console.log('⏳ Swiper not loaded yet, loading...');
          // Cargar Swiper CSS si no está
          if (!document.querySelector('link[href*="swiper-bundle.min.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css';
            document.head.appendChild(link);
          }
          // Cargar Swiper JS si no está
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';
          script.onload = () => {
            this.createSwiperInstance(swiperContainer);
          };
          document.head.appendChild(script);
        } else {
          this.createSwiperInstance(swiperContainer);
        }
      }

      // Crear instancia de Swiper
      createSwiperInstance(swiperContainer) {
        console.log('🎠 Initializing Swiper in modal...');

        // Buscar los botones de navegación dentro del modal
        const prevButton = this.modalContent.querySelector('.swiper-button-prev');
        const nextButton = this.modalContent.querySelector('.swiper-button-next');

        try {
          const swiperInstance = new Swiper(swiperContainer, {
            slidesPerView: 3,
            spaceBetween: 18,
            navigation: {
              nextEl: nextButton,
              prevEl: prevButton
            },
            // Opciones adicionales para mejor rendimiento en modal
            observer: true,
            observeParents: true,
            resizeObserver: true
          });

          console.log('✅ Swiper initialized in modal successfully');
        } catch (error) {
          console.error('❌ Error initializing Swiper in modal:', error);
        }
      }

      // Natulim SP - 18-nov-25: Fix videos circulares en modal de vista rápida  
      initializeCircleVideos() {
        console.log('🎬 Re-initializing Circle Videos in modal...');

        // Solo buscar videos en el modal y re-aplicar listeners
        const videoPlayer = this.modalContent.querySelectorAll(".storiesVideo");

        if (videoPlayer.length === 0) {
          console.log('ℹ️ No videos found in modal');
          return;
        }

        // Verificar si el modal global existe, si no, crearlo
        let modalWrapperVideo = document.querySelector('.modalWrapperVideo');
        let modalContainerVideos = document.querySelector('.modalContainerVideos');

        if (!modalWrapperVideo || !modalContainerVideos) {
          console.log('🔧 Creating global video modal...');
          this.createGlobalVideoModal();
          modalWrapperVideo = document.querySelector('.modalWrapperVideo');
          modalContainerVideos = document.querySelector('.modalContainerVideos');
        }

        // Re-aplicar los event listeners
        videoPlayer.forEach(video => {
          video.pause();

          // Click listener para abrir modal de video
          video.addEventListener("click", () => {
            let bigvideosrc = video.dataset.video;

            // Usar la lógica existente
            modalContainerVideos.querySelectorAll(`video`).forEach(bigvideos => {
              bigvideos.pause();
              bigvideos.classList.add("hidden");
            });

            let searchexist = modalContainerVideos.querySelector(`video[src="${bigvideosrc}"]`);
            if (Boolean(searchexist)) {
              searchexist.play();
              searchexist.classList.remove("hidden");
              modalWrapperVideo.classList.remove("hidden");
            } else {
              const bigVideo = document.createElement("video");
              bigVideo.src = bigvideosrc;
              bigVideo.loop = true;
              modalContainerVideos.appendChild(bigVideo);
              modalWrapperVideo.classList.remove("hidden");
              bigVideo.play();
            }
          });

          // Ended listener para auto-play siguiente
          video.addEventListener("ended", () => {
            if (typeof window.playNextVideo === 'function') {
              window.playNextVideo();
            } else {
              // Fallback simple si la función global no existe
              this.playNextVideoFallback(videoPlayer, video);
            }
          });
        });

        console.log('✅ Circle Videos re-initialized successfully');
      }

      // Natulim SP - 18-nov-25: Fix videos circulares en modal de vista rápida
      // Crea el modal global de videos si no existe, replicando la funcionalidad de main-product.liquid
      createGlobalVideoModal() {
        // Crear el modal wrapper principal
        const modalWrapperVideo = document.createElement("div");
        modalWrapperVideo.classList.add("modalWrapperVideo", "hidden");

        // Crear el contenedor de videos
        const modalContainerVideos = document.createElement("div");
        modalContainerVideos.classList.add("modalContainerVideos");

        // Crear botón de cerrar
        const closeModalVideo = document.createElement("div");
        closeModalVideo.classList.add("closeModalVideo");
        closeModalVideo.innerHTML = '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.055 16.594a.662.662 0 0 1-.899 0L10 11.398l-5.195 5.196a.662.662 0 0 1-.899 0 .662.662 0 0 1 0-.899L9.102 10.5 3.906 5.344a.662.662 0 0 1 0-.899.662.662 0 0 1 .899 0L10 9.641l5.156-5.196a.662.662 0 0 1 .899 0 .662.662 0 0 1 0 .899L10.859 10.5l5.196 5.195a.662.662 0 0 1 0 .899Z" style="fill: #FFFFFF !important;"></path></svg>';

        // Event listener para cerrar
        closeModalVideo.addEventListener("click", e => {
          modalContainerVideos.querySelectorAll("video").forEach(vid => vid.pause());
          modalWrapperVideo.classList.add("hidden");
        });

        // Obtener información del producto desde el modal
        const productInfo = this.modalContent.querySelector('product-info');
        const productTitle = productInfo?.querySelector('h1, h2')?.textContent || 'Producto';
        const productImage = productInfo?.querySelector('.product__media img')?.src || '';
        const productPrice = productInfo?.querySelector('.price')?.textContent || '';

        // Crear wrapper del producto
        const productWrapperModalVideo = document.createElement("div");
        productWrapperModalVideo.innerHTML = `
        <div class="flex videoButtons">
          <button class="toggleBoxProduct" onclick="window.toggleBoxProduct && window.toggleBoxProduct(this)">
            <span>Ver producto</span>
            <svg width="24" height="24" viewBox="0 0 44.308 44.308" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M31.767 20.849 16.727 5.809a0.923 0.923 0 0 0 -1.305 0l-1.305 1.305a0.923 0.923 0 0 0 0 1.305L27.851 22.154l-13.735 13.734a0.923 0.923 0 0 0 0 1.305l1.306 1.305a0.923 0.923 0 0 0 1.305 0l15.04 -15.04a1.846 1.846 0 0 0 0 -2.61Z"></path></svg>
          </button>
          <button class="toggleSound" onclick="window.toggleSound && window.toggleSound(this)">
            <svg class="soundDisable" width="20" height="20" viewBox="0 0 24 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.508.867C12.313.79 12.156.79 12 .79c-.313 0-.625.078-.86.313L5.868 5.75H2.625C1.57 5.75.75 6.61.75 7.625v3.71c0 1.017.82 1.837 1.875 1.837h3.242l5.274 4.687c.234.196.546.313.859.313.156 0 .313-.04.508-.117.43-.196.703-.664.703-1.133V2c0-.469-.274-.938-.703-1.133Zm-1.133 14.766-4.766-4.258H2.625v-3.71h3.984l4.766-4.259v12.227ZM20.789 9.5l1.836-1.836c.39-.39.39-.976 0-1.328-.352-.39-.938-.39-1.328 0L19.5 8.21l-1.875-1.875c-.39-.352-.977-.352-1.328 0-.39.39-.39.976 0 1.328L18.133 9.5l-1.836 1.836c-.39.39-.39.976 0 1.328.351.39.937.39 1.328 0l1.875-1.836 1.836 1.836c.352.39.937.39 1.289 0 .39-.351.39-.937 0-1.328L20.789 9.5Z" fill="#fff"></path></svg>
            <svg class="soundEnable" width="20" height="17" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.758.867A1.349 1.349 0 0 0 11.25.75c-.313 0-.625.117-.86.352L5.118 5.75H1.875C.82 5.75 0 6.61 0 7.625v3.71c0 1.017.82 1.837 1.875 1.837h3.242l5.274 4.687c.234.313.546.391.859.391.156 0 .313 0 .508-.078.43-.235.742-.664.742-1.172V2c0-.469-.313-.938-.742-1.133Zm-1.133 14.766-4.766-4.258H1.875v-3.71h3.984l4.766-4.259v12.227Zm5.469-9.024a1.053 1.053 0 0 0-.586-.195.962.962 0 0 0-.742.352.942.942 0 0 0 .156 1.328c.43.351.703.86.703 1.406a1.85 1.85 0 0 1-.703 1.445.942.942 0 0 0-.156 1.328.962.962 0 0 0 .742.352c.195 0 .43-.078.586-.195.898-.742 1.406-1.797 1.406-2.93 0-1.094-.508-2.148-1.406-2.89Zm2.383-2.89c-.196-.117-.391-.196-.625-.196-.274 0-.508.118-.704.352-.351.39-.273.977.118 1.328 1.328 1.055 2.109 2.656 2.109 4.297 0 1.68-.781 3.281-2.11 4.336-.39.351-.468.937-.117 1.328.196.234.43.352.704.352.234 0 .43-.079.625-.196 1.757-1.445 2.773-3.554 2.773-5.82 0-2.227-1.016-4.336-2.773-5.781Z" fill="#fff"></path></svg>
          </button>
        </div>
        <div class="productBoxModal">
          <img src="${productImage}"/>
          <div class="productBoxModalText">
            <h3>${productTitle}</h3>
            <div class="modalBoxAddtoCart">
              <span>${productPrice}</span>
              <button onclick="window.addToCart && window.addToCart()">Añadir al carrito</button>
            </div>
          </div>
        </div>`;
        productWrapperModalVideo.classList.add("productWrapperModalVideo");

        // Crear botón play/pause
        const playPauseVideo = document.createElement("div");
        playPauseVideo.innerHTML = `
          <svg width="90" height="90" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50.387 85.167c-3.142 0-5.387-2.47-5.387-5.387V40.275C45 37.469 47.245 35 50.387 35c.898 0 1.908.224 2.806.786l32.322 19.752c1.571.898 2.581 2.694 2.581 4.49 0 1.907-1.01 3.703-2.581 4.6L53.193 84.382a5.33 5.33 0 0 1-2.806.786Z" fill="#fff"></path></svg>
        `;
        playPauseVideo.classList.add("playPauseVideo");
        playPauseVideo.addEventListener("click", e => {
          playPauseVideo.classList.toggle("pauseVideo");
          modalWrapperVideo.querySelectorAll('video').forEach(video => {
            if (playPauseVideo.classList.contains("pauseVideo")) {
              video.pause();
            } else {
              modalContainerVideos.querySelector(`video:not(.hidden)`).play();
            }
          });
        });

        // Ensamblar el modal
        modalWrapperVideo.appendChild(closeModalVideo);
        modalWrapperVideo.appendChild(modalContainerVideos);
        modalWrapperVideo.appendChild(productWrapperModalVideo);
        modalWrapperVideo.appendChild(playPauseVideo);

        // Agregar al DOM
        document.body.appendChild(modalWrapperVideo);

        // Crear funciones globales si no existen
        if (!window.toggleBoxProduct) {
          window.toggleBoxProduct = function (pelem) {
            pelem.closest('.productWrapperModalVideo').classList.toggle("hideProductBoxModal");
          }
        }

        if (!window.toggleSound) {
          window.toggleSound = function (pelem) {
            pelem.closest('.productWrapperModalVideo').classList.toggle("disableSound");
            pelem.classList.toggle("disableSoundBtn");
            pelem.closest('.modalWrapperVideo').querySelectorAll("video").forEach(video => {
              if (pelem.classList.contains("disableSoundBtn")) {
                video.muted = true;
              } else {
                video.muted = false;
              }
            });
          }
        }

        if (!window.addToCart) {
          window.addToCart = function () {
            // Find the add to cart button in the modal and click it
            const modal = document.querySelector('quick-add-modal');
            const addToCartBtn = modal?.querySelector('button[name="add"], .btn.product-form__cart-submit');
            if (addToCartBtn) {
              addToCartBtn.click();
            }
          }
        }


        console.log('✅ Global video modal created successfully');
      }

      // Natulim SP - 19-nov-25: Fix autoplay videos en modal de vista rápida
      // Basado en la solución que funciona en natulim-ro\assets\quick-add-modal.js
      initializeVideos() {
        // Reproducir solo los videos con atributo autoplay (no los videos circulares)
        const autoplayVideos = this.modalContent.querySelectorAll('video[autoplay]');
        autoplayVideos.forEach(video => {
          if (video.muted) {
            // Solo reproducir si está muted (política de navegadores)
            video.play().catch(e => console.log('Video autoplay blocked:', e));
          }
        });
      }

      // Natulim SP - 18-nov-25: Fix videos circulares en modal de vista rápida
      playNextVideoFallback(videoPlayer, currentVideo) {
        const currentIndex = Array.from(videoPlayer).indexOf(currentVideo);
        const nextIndex = (currentIndex + 1) % videoPlayer.length;
        const nextVideo = videoPlayer[nextIndex];

        if (nextVideo && nextVideo.dataset.src) {
          if (!nextVideo.src) {
            nextVideo.src = nextVideo.dataset.src;
          }
          nextVideo.play();
        }
      }

      // Natulim DB | 28 enero 2026: Ajuste de seguridad para sincronización
      initializePriceSync() {
        // Usamos requestAnimationFrame para esperar a que el navegador haya pintado el modal
        requestAnimationFrame(() => {
          if (window.initNatulimPriceObserver) {
            window.initNatulimPriceObserver(this.modalContent);
          }
          if (window.fixCurrencyAndPrice) {
            // Ejecutamos dos veces para asegurar que Dawn no nos sobreescriba
            window.fixCurrencyAndPrice();
            setTimeout(() => window.fixCurrencyAndPrice(), 100);
          }
        });
      }
    }
  );
}
